import { reviewRequestService } from '@/services/ReviewRequestService';
import { featureUnlockService } from '@/services/FeatureUnlockService';
import { insightReviewService } from '@/services/InsightReviewService';
import { EventBus } from '@/main';

interface PendingUnlock {
  featureCode: string;
  storeId: number;
  unlockedAt: number;
}

const STORAGE_KEY_PREFIX = 'pending_feature_unlocks';

class PostUnlockReviewService {
  private triggeredThisSession: Set<string> = new Set();

  private compositeKey(storeId: number, featureCode: string): string {
    return `${featureCode}|${storeId}`;
  }

  private storageKey(storeId: number): string {
    return `${STORAGE_KEY_PREFIX}:${storeId}`;
  }

  /**
   * Register a feature unlock for recurring review requests.
   * Called after successful survey submission in FeatureUnlockModal.
   * Persists in localStorage so review prompts continue across sessions.
   */
  public registerUnlock(featureCode: string, storeId: number): void {
    const pending = this.getPendingUnlocks(storeId);
    if (!pending.find(p => p.featureCode === featureCode && p.storeId === storeId)) {
      pending.push({ featureCode, storeId, unlockedAt: Date.now() });
      this.savePendingUnlocks(storeId, pending);
    }
  }

  /**
   * Check if a review should be requested and trigger it.
   * Called from feature use points (e.g., after download completes).
   *
   * Requests review once per week until merchant clicks "Leave a Review".
   * Backend enforces 7-day cooldown and checks review_outcome='link_opened'.
   */
  public async checkAndTriggerReview(storeId: number, featureCode: string): Promise<void> {
    const key = this.compositeKey(storeId, featureCode);

    if (this.triggeredThisSession.has(key)) {
      return;
    }

    const pending = this.getPendingUnlocks(storeId);
    const match = pending.find(p => p.featureCode === featureCode && p.storeId === storeId);
    if (!match) {
      return;
    }

    try {
      const eligibility = await reviewRequestService.checkEligibility('feature_unlock');

      if (!eligibility.eligible) {
        // Stop asking permanently if merchant already clicked review link
        if (eligibility.reason === 'review_completed') {
          this.removePendingUnlock(storeId, featureCode);
        }
        return;
      }

      this.triggeredThisSession.add(key);

      // Mark review_requested on backend (fire-and-forget)
      featureUnlockService.updateReviewStatus(featureCode, true).catch(() => {});

      // Wait for the success state to settle
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Record the ask timestamp so insight triggers can cross-check and skip
      insightReviewService.recordFeatureUnlockAsk(storeId);

      // Show review request dialog
      EventBus.$emit('show-review-request-dialog', {
        storeId,
        triggerEvent: `feature_unlock_${featureCode}`
      });
    } catch (error) {
      console.error('[PostUnlockReview] Error triggering review:', error);
    }
  }

  /**
   * Check if a feature has a pending unlock (review not yet completed).
   */
  public hasPendingUnlock(featureCode: string, storeId?: number): boolean {
    if (storeId) {
      const pending = this.getPendingUnlocks(storeId);
      return pending.some(p => p.featureCode === featureCode && p.storeId === storeId);
    }
    // Fallback: check all stores (for backward compat)
    return this.getLegacyPendingUnlocks().some(p => p.featureCode === featureCode);
  }

  private getPendingUnlocks(storeId: number): PendingUnlock[] {
    try {
      const raw = localStorage.getItem(this.storageKey(storeId));
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as PendingUnlock[]) : [];
      }
      // Migrate legacy data (unscoped key) on first access
      return this.migrateLegacyData(storeId);
    } catch {
      return [];
    }
  }

  private getLegacyPendingUnlocks(): PendingUnlock[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PREFIX);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? (parsed as PendingUnlock[]) : [];
    } catch {
      return [];
    }
  }

  private migrateLegacyData(storeId: number): PendingUnlock[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PREFIX);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Add storeId to legacy entries and save under new key
      const migrated = (parsed as any[]).map(p => ({
        featureCode: p.featureCode,
        storeId: p.storeId || storeId,
        unlockedAt: p.unlockedAt,
      }));
      this.savePendingUnlocks(storeId, migrated);
      // Remove legacy key
      localStorage.removeItem(STORAGE_KEY_PREFIX);
      return migrated;
    } catch {
      return [];
    }
  }

  private savePendingUnlocks(storeId: number, pending: PendingUnlock[]): void {
    try {
      localStorage.setItem(this.storageKey(storeId), JSON.stringify(pending));
    } catch {
      // localStorage unavailable — non-critical
    }
  }

  private removePendingUnlock(storeId: number, featureCode: string): void {
    const pending = this.getPendingUnlocks(storeId).filter(
      p => !(p.featureCode === featureCode && p.storeId === storeId)
    );
    this.savePendingUnlocks(storeId, pending);
  }
}

export const postUnlockReviewService = new PostUnlockReviewService();
